import React, { useId, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Trash2, Search } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import LoadFailure from "../../components/ui/LoadFailure";
import { useT } from "../../lib/LanguageContext";
import { platformMediaSrc } from "../../lib/platformMedia";
import { HQ_SITE } from "../../constants/testIds/hqSite";
import siteApi from "./siteApi";
import usePlatformMedia from "./usePlatformMedia";
import MediaUploadButton from "./MediaUploadButton";
import { mediaInUseReferences, siteErrorMessage } from "./siteErrors";

/**
 * Media tab: the platform media library (contract §3.4). Upload, alt text per locale,
 * where an asset is used, and delete — which the server refuses (409 MEDIA_IN_USE) while
 * anything still references the asset; the refusal's references are listed on the card so
 * the operator knows what to change first.
 */

const kilobytes = (bytes) => `${Math.max(1, Math.round(Number(bytes) / 1024))} KB`;

function ReferenceList({ references, t, testId }) {
  const typeLabels = {
    content_block: t("hsite.media.ref.content_block"),
    blog_post: t("hsite.media.ref.blog_post"),
    plan: t("hsite.media.ref.plan"),
    booster: t("hsite.media.ref.booster"),
  };
  if (references.length === 0) {
    return <p className="text-xs text-slate-400" data-testid={testId}>{t("hsite.media.unused")}</p>;
  }
  return (
    <ul className="text-xs text-slate-600 space-y-0.5" data-testid={testId}>
      {references.map((r) => (
        <li key={`${r.type}-${r.id}`}>
          <span className="text-slate-400">{typeLabels[r.type] || r.type}:</span> {r.label || r.id}
        </li>
      ))}
    </ul>
  );
}

function MediaCard({ item, schema, media, t, readOnly }) {
  const locales = schema.locales;
  const altKey = (l) => `alt_${l}`;
  const stored = Object.fromEntries(locales.map((l) => [altKey(l), item[altKey(l)] || ""]));
  const [alt, setAlt] = useState(stored);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [references, setReferences] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const baseId = useId();
  const max = schema.limits.alt;
  const dirty = locales.some((l) => alt[altKey(l)] !== stored[altKey(l)]);
  const tooLong = locales.some((l) => alt[altKey(l)].length > max);

  const run = async (work) => {
    setBusy(true);
    setError("");
    try {
      await work();
    } catch (e) {
      setError(siteErrorMessage(e, t));
      const inUse = mediaInUseReferences(e);
      if (inUse.length > 0) setReferences(inUse);
    } finally {
      setBusy(false);
    }
  };

  const saveAlt = () => run(async () => {
    const patch = Object.fromEntries(locales.filter((l) => alt[altKey(l)] !== stored[altKey(l)]).map((l) => [altKey(l), alt[altKey(l)]]));
    await media.updateAlt(item.media_id, patch);
    toast.success(t("hsite.media.altSaved"));
  });

  const showUsage = () => run(async () => { setReferences(await media.usage(item.media_id)); });

  const remove = () => run(async () => {
    setConfirmDelete(false);
    await media.remove(item.media_id);
    toast.success(t("hsite.media.deleted"));
  });

  return (
    <div className="rounded-xl bg-white border border-slate-200 overflow-hidden flex flex-col" data-testid={HQ_SITE.mediaCard(item.media_id)}>
      <img src={platformMediaSrc(item.url)} alt="" className="w-full h-36 object-cover bg-slate-50" loading="lazy" />
      <div className="p-3 space-y-2 flex-1 flex flex-col">
        <div className="text-xs text-slate-600 truncate" title={item.original_name}>{item.original_name}</div>
        <div className="text-[11px] text-slate-400">{item.content_type} · {kilobytes(item.size)}</div>
        {locales.map((l) => (
          <div key={l}>
            <label htmlFor={`${baseId}-${l}`} className="block text-[10px] uppercase tracking-wider text-slate-400">
              {t("hsite.media.alt", { locale: l.toUpperCase() })}
            </label>
            <Input
              id={`${baseId}-${l}`}
              value={alt[altKey(l)]}
              onChange={(e) => {
                const next = e.target.value;
                setAlt((prev) => ({ ...prev, [altKey(l)]: next }));
              }}
              className="h-8 text-xs bg-white border-slate-200"
              readOnly={readOnly}
              data-testid={HQ_SITE.mediaAlt(item.media_id, l)}
            />
          </div>
        ))}
        {tooLong && <p role="alert" className="text-xs text-rose-600">{t("hsite.media.altTooLong", { max })}</p>}
        <div className="flex flex-wrap gap-2 pt-1">
          {!readOnly && <Button
            type="button"
            size="sm"
            onClick={saveAlt}
            disabled={busy || !dirty || tooLong}
            className="bg-sol-gold text-black hover:bg-sol-gold/90 h-7 text-xs rounded-xl"
            data-testid={HQ_SITE.mediaAltSave(item.media_id)}
          >
            <Save className="w-3 h-3 mr-1" /> {t("hsite.media.saveAlt")}
          </Button>}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={showUsage}
            disabled={busy}
            className="h-7 text-xs rounded-xl"
            data-testid={HQ_SITE.mediaUsage(item.media_id)}
          >
            <Search className="w-3 h-3 mr-1" /> {t("hsite.media.usage")}
          </Button>
          {!readOnly && <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={confirmDelete ? remove : () => setConfirmDelete(true)}
            disabled={busy}
            className={`h-7 text-xs rounded-xl ${confirmDelete ? "border-rose-300 text-rose-600" : "text-rose-500"}`}
            data-testid={HQ_SITE.mediaDelete(item.media_id)}
          >
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
            {confirmDelete ? t("hsite.media.confirmDelete") : t("hsite.media.delete")}
          </Button>}
        </div>
        {error && <p role="alert" className="text-xs text-rose-600" data-testid={HQ_SITE.mediaError(item.media_id)}>{error}</p>}
        {references && (
          <ReferenceList references={references} t={t} testId={HQ_SITE.mediaReferences(item.media_id)} />
        )}
      </div>
    </div>
  );
}

export default function MediaTab({ schema, api = siteApi, readOnly = false }) {
  const t = useT("superadmin");
  const media = usePlatformMedia({ api });

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <p className="text-sm text-slate-500 max-w-2xl">{t("hsite.media.hint")}</p>
        {!readOnly && <MediaUploadButton schema={schema} upload={media.upload} testId={HQ_SITE.mediaUpload} />}
      </div>
      {media.status === "loading" && <Loader2 className="w-6 h-6 animate-spin text-amber-600" />}
      <LoadFailure
        message={media.status === "error" ? siteErrorMessage(media.error, t) : ""}
        onRetry={media.reload}
        retryLabel={t("hsite.retry")}
      />
      {media.status === "ready" && media.items.length === 0 && (
        <p className="text-slate-400 text-sm py-12 text-center" data-testid={HQ_SITE.mediaEmpty}>{t("hsite.media.empty")}</p>
      )}
      {media.status === "ready" && media.items.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {media.items.map((item) => (
            <MediaCard key={item.media_id} item={item} schema={schema} media={media} t={t} readOnly={readOnly} />
          ))}
        </div>
      )}
    </div>
  );
}
